import { useEffect, useMemo, useRef, useState } from "react";
import type { EspecialidadOption } from "./useEspecialidades";

export function useEspecialidadesSelection(params: {
  especialidades: EspecialidadOption[];
  initialSelected?: string[];
  disabled?: boolean;
}) {
  const { especialidades, initialSelected = [], disabled = false } = params;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelected)
  );

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setSelectedIds(new Set(initialSelected));
  }, [initialSelected]);

  useEffect(() => {
    if (especialidades.length === 0) return;

    setSelectedIds((prev) => {
      const valid = new Set(especialidades.map((e) => e.id));
      const next = new Set<string>();

      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
      });

      if (next.size === prev.size) {
        let same = true;
        prev.forEach((id) => {
          if (!next.has(id)) same = false;
        });
        if (same) return prev;
      }

      return next;
    });
  }, [especialidades]);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const moveToSelected = (ids: string[]) => {
    if (disabled || ids.length === 0) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const moveToAvailable = (ids: string[]) => {
    if (disabled || ids.length === 0) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const clear = () => setSelectedIds(new Set());

  return {
    selectedIds,
    selectedCount,
    setSelectedIds,
    moveToSelected,
    moveToAvailable,
    clear,
  };
}